//------------------------------------------------------------------------------
// name: BankSelector.tsx
// author: mudas
// created: 2025/12/09
//------------------------------------------------------------------------------

import React, { FC } from 'react';
import { useQuery } from '@tanstack/react-query';

import { useProxyValue } from 'common/components';
import { selectFilterOption } from 'common/helper/search';

import { Identity } from 'common/types';
import { SimpleValueType, ProxyValue } from 'common/components';
import service from 'common/service';

import { Select, SelectProps } from '@fuxi/eevee-ui';

type ValueType = SimpleValueType;
type OptionType = Identity;

export interface IProps extends Omit<SelectProps<ValueType, OptionType>, 'onChange' | 'value'> {
  // 是否返回 option 替代 value
  replaceAsOption?: boolean;

  // 受 replaceAsOption 影响，value 为 option 时，onChange 回调参数为 option，否则为 value
  value?: ProxyValue<OptionType>;
  onChange?: (value: ProxyValue<OptionType>) => void;
}

const BankSelector: FC<IProps> = (props) => {
  const { replaceAsOption, value: baseValue, onChange: baseOnChange, ...otherProps } = props;

  const { selectValue, onChange } = useProxyValue<OptionType>({
    fieldName: 'bankId',
    replaceAsOption,
    value: baseValue,
    onChange: baseOnChange,
  });

  const { data, isFetching, isError, refetch } = useQuery({
    // queryKey 与 FillApllyInfoModal 里 queryClient.fetchQuery(['getBankList']) 保持一致，共用同一份缓存
    queryKey: ['getBankList'],
    queryFn: async () => {
      const res = await service.userSetting.getBankList();
      return res.data;
    },
    cacheTime: 10 * 60 * 1000,
    staleTime: 10 * 60 * 1000,
  });

  return (
    <Select<ValueType, OptionType>
      loading={isFetching}
      options={data?.items}
      // 失败时不要再伪装成「暂无数据」，否则线上完全看不出异常
      notFoundContent={isError ? '银行列表加载失败，请重新展开重试' : undefined}
      onOpenChange={(open) => {
        // 失败后重新展开下拉时再试一次，避免一直停在空列表上无法自愈
        if (open && isError) refetch();
      }}
      value={selectValue}
      showSearch
      allowClear
      placeholder="请选择银行"
      fieldNames={{ label: 'bankName', value: 'bankId' }}
      filterOption={selectFilterOption({ label: 'bankName', value: 'bankId' })}
      onChange={onChange}
      {...otherProps}
    />
  );
};

export default BankSelector;
